/**
 * Sons de jeu synthétisés en **Web Audio** — aucun fichier audio (0 Ko d'asset, cohérent
 * avec le parti « tout vectoriel / zéro chargement » du projet ; satisfait « < 30 Ko » du
 * plan de façon triviale). Samples courts (< 300 ms), jamais de musique de fond, jamais de
 * son sur la navigation d'interface (docs/05 §8). Coupé par le réglage Son (F-SETTINGS-001)
 * et le mode silencieux OS. Un même son ne rejoue pas avant 50 ms.
 */
import { getSettings } from './settings';

export type SoundName = 'tap' | 'refuse' | 'lifeLost' | 'win' | 'round' | 'room';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
const lastAt: Partial<Record<SoundName, number>> = {};
const THROTTLE_MS = 50;

function nowMs(): number {
  try {
    return performance.now();
  } catch {
    return 0;
  }
}

/**
 * Crée/réveille le contexte audio au 1er geste utilisateur (politique autoplay des
 * navigateurs : impossible avant une interaction). Renvoie `false` si le son est coupé ou
 * indisponible.
 */
function ensureCtx(): boolean {
  if (!getSettings().sound) return false;
  try {
    if (!ctx) {
      const AC =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return false;
      ctx = new AC();
      master = ctx.createGain();
      master.connect(ctx.destination);
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return true;
  } catch {
    return false;
  }
}

/** À appeler sur le 1er `pointerdown` pour débloquer l'audio le plus tôt possible. */
export function primeAudio(): void {
  ensureCtx();
}

interface ToneOpts {
  type?: OscillatorType;
  /** Glissando : fréquence de départ (défaut = fréquence cible). */
  from?: number;
  /** Pic de gain (0..1) relatif au volume maître. */
  gain?: number;
  /** Décalage de départ, en secondes. */
  delay?: number;
}

/** Une note enveloppée (attaque rapide, décroissance exponentielle). */
function tone(freq: number, dur: number, o: ToneOpts = {}): void {
  if (!ctx || !master) return;
  const t0 = ctx.currentTime + (o.delay ?? 0);
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = o.type ?? 'sine';
  const f0 = o.from ?? freq;
  osc.frequency.setValueAtTime(f0, t0);
  if (o.from != null && o.from !== freq) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, freq), t0 + dur);
  }
  const peak = o.gain ?? 0.2;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g);
  g.connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.03);
}

const R = (a: number, b: number): number => a + Math.random() * (b - a);

/** Joue un effet (respecte le réglage Son, le volume et l'anti-répétition 50 ms). */
export function playSound(name: SoundName): void {
  if (!ensureCtx() || !ctx || !master) return;
  const t = nowMs();
  const prev = lastAt[name];
  if (prev != null && t - prev < THROTTLE_MS) return;
  lastAt[name] = t;

  master.gain.setValueAtTime(getSettings().volume, ctx.currentTime);

  switch (name) {
    case 'tap': // tic doux, léger aléa de pitch
      tone(R(500, 560), 0.06, { type: 'triangle', gain: 0.14 });
      break;
    case 'refuse': // toc mat grave
      tone(150, 0.11, { type: 'square', gain: 0.12 });
      break;
    case 'lifeLost': // note descendante
      tone(200, 0.26, { type: 'sine', from: 430, gain: 0.2 });
      break;
    case 'win': // carillon 3 notes montantes (C5–E5–G5)
      tone(523.25, 0.16, { type: 'sine', gain: 0.18 });
      tone(659.25, 0.16, { type: 'sine', gain: 0.18, delay: 0.11 });
      tone(783.99, 0.3, { type: 'sine', gain: 0.2, delay: 0.22 });
      break;
    case 'round': // manche Marathon réussie : 1 note
      tone(659.25, 0.16, { type: 'sine', gain: 0.18 });
      break;
    case 'room': // création/entrée en salle : pop discret
      tone(392, 0.09, { type: 'triangle', from: 300, gain: 0.14 });
      break;
  }
}
